<script setup lang="ts">
import { ApiPutMateMail } from "@/apis/interaction";
import { SettlementStatus } from "@/enums/mateEnums";
import {
  DispatchStatus,
  InteractionEventStatus,
  InteractionEventType,
  InvitationStatus,
} from "@/enums/mission";
import { useInteractiveReview } from "@/stores/interactive-review";
import {
  InteractionEvent,
  MateDispatch,
  MateInvitation,
  MateMail,
} from "@/types/entity";
import MateMailEditPopup from "@/views/Mate/Mail/MateMailEditPopup.vue";
import { showFailToast, showLoadingToast } from "vant";
import {
  computed,
  nextTick,
  onActivated,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import ThreadList, { ThreadListInstance } from "../mobile/ThreadList.vue";

const reviewStore = useInteractiveReview();
const eventListRef = ref<ThreadListInstance | null>(null);

const props = withDefaults(
  defineProps<{
    scrollerRef?: HTMLElement | null;
    enablePullLoad?: boolean;
    enableFilterMate?: boolean;
    mateId?: number;
  }>(),
  {
    enableFilterMate: true,
    enablePullLoad: true,
  }
);

const emit = defineEmits<{
  (e: "reload"): void;
}>();

const hasNewData = ref(false);

const reload = async () => {
  await reviewStore.initOrFetchList(null, true, activeMateId.value);
  eventListRef.value?.resetScrollLoadingState();
  emit("reload");
};

const onReviewEvent = () => {
  eventListRef.value?.resetScrollLoadingState();
  hasNewData.value = false;
};

onMounted(async () => {
  reviewStore.eventBus.on(onReviewEvent);
  await reviewStore.initOrFetchList(null, true, activeMateId.value);
  await nextTick();
  eventListRef.value?.resetScrollLoadingState();
});

onActivated(async () => {
  const list: HTMLElement = document.querySelector(".interaction-event-list")!;
  if (list.scrollTop === 0) {
    // 1. 如果列表在顶部，则刷新列表
    await reviewStore.initOrFetchList(null, true, activeMateId.value);
    await nextTick();
    eventListRef.value?.resetScrollLoadingState();
  } else {
    // 2. 如果列表不在顶部，则后台请求一次，判断是否有新数据
    const newData = await reviewStore.fetchList(null, 1, undefined, 5);
    if (newData.length === 0) return;
    hasNewData.value = newData[0].id !== reviewStore.all.ids[0];
  }
});

onUnmounted(() => {
  reviewStore.eventBus.off(onReviewEvent);
});

const showMailEditPopup = ref(false);
const targetInteractionEvent = ref<InteractionEvent | null>(null);
const targetMateMail = computed<MateMail | null>(() => {
  return targetInteractionEvent.value?.entity as MateMail | null;
});

const startEditMail = async (interactionEvent: InteractionEvent) => {
  targetInteractionEvent.value = interactionEvent;
  await nextTick();

  if (!targetMateMail.value?.sender) {
    showFailToast("该用户状态异常，暂无法回信。");
    return;
  }

  showMailEditPopup.value = true;
};

const onSaveMail = async (mateMail: MateMail) => {
  const loading = showLoadingToast("");
  try {
    const old = reviewStore.entities[targetInteractionEvent.value!.id];
    old.entity = {
      ...old.entity,
      ...mateMail,
    };
    await ApiPutMateMail(old.entity.id, {
      subject: mateMail.subject,
      body: mateMail.body,
    });
  } finally {
    loading.close();
  }
};

const isNeedAction = (event: InteractionEvent): boolean => {
  if (
    event.type === InteractionEventType.StrollReturned &&
    (event.entity as MateDispatch)?.status === DispatchStatus.Returned
  ) {
    // 溜达返回
    return true;
  } else if (
    event.type === InteractionEventType.ReceivedLetter &&
    !(event.entity as MateMail).dtRead
  ) {
    // 未读信件
    return true;
  } else if (event.type === InteractionEventType.MissionReturned) {
    const entity = event.entity as MateDispatch;
    if (entity.dispatchMemberzz?.length) {
      const currentMate = entity.dispatchMemberzz?.find(
        (v) => v.mateId === event.mateId
      );
      return currentMate?.settlementStatus === SettlementStatus.Pending;
    }
  } else if (event.type === InteractionEventType.ReceivedMissionInvitation) {
    const entity = event.entity as MateInvitation;
    return entity.status === InvitationStatus.Pending;
  }
  return (
    (event.type === InteractionEventType.RequestLetterSending ||
      event.type === InteractionEventType.RequestStroll ||
      event.type === InteractionEventType.FriendInvitationSent ||
      event.type === InteractionEventType.RequestMomentPost ||
      event.type === InteractionEventType.RequestMomentComment ||
      event.type === InteractionEventType.RequestMission) &&
    event.reviewStatus === InteractionEventStatus.Pending
  );
};

const filterMateId = ref<number | undefined>(undefined);
const activeMateId = computed(() => {
  return props.enableFilterMate ? filterMateId.value : props.mateId;
});
const showOcSelector = ref(false);
const onConfirmOc = (value?: number) => {
  filterMateId.value = value;
  showOcSelector.value = false;
};

watch(activeMateId, () => {
  reload();
});

export interface InteractionEventListInstance {
  reload: () => void;
}

defineExpose<InteractionEventListInstance>({
  reload,
});

const scrollToTopAndReload = async () => {
  const scroller = document.querySelector(".interaction-event-list");
  const onScrollTop = () => {
    if (scroller?.scrollTop === 0) {
      nextTick(() => {
        eventListRef.value?.onPullRefresh();
        scroller?.removeEventListener("scroll", onScrollTop);
      });
    }
  };
  if (scroller && scroller.scrollTop !== 0) {
    scroller.addEventListener("scroll", onScrollTop);
  }
  eventListRef.value?.scrollToTop();
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="enableFilterMate" class="flex items-center justify-between px-2">
      <SelectAllMate
        :active-mate-id="activeMateId"
        v-model:show="showOcSelector"
        @confirm="onConfirmOc"
      />
      <div
        v-if="hasNewData"
        class="btn btn-xs btn-outline rounded"
        @click="scrollToTopAndReload"
      >
        <i class="iconify mingcute--arrow-up-line" />
        <span>新事件</span>
      </div>
    </div>
    <ThreadList
      ref="eventListRef"
      :on-pull-load="() => reload()"
      :on-scroll-load="() => reviewStore.fetchListNextPage(null, activeMateId)"
      :immediate-check="false"
      :enable-pull-load="props.enablePullLoad"
      enable-scroll-record
      :scroller="scrollerRef"
      class="w-full interaction-event-list"
    >
      <slot />
      <div class="m-2 mr-0 flex flex-col gap-2 border-l-2 px-2">
        <InteractionItem
          v-for="event in reviewStore.allList"
          :key="event.id"
          :event="event"
          :is-need-action="isNeedAction(event)"
          @edit-mail="startEditMail"
          :class="{
            'bg-transparent shadow-none': !isNeedAction(event),
          }"
          class="relative before:content-[''] before:w-2 before:h-2 before:bg-primary before:rounded-full before:block before:absolute before:-left-[13px] before:top-5"
        />
      </div>
    </ThreadList>
    <MateMailEditPopup
      v-if="targetMateMail"
      v-model:show="showMailEditPopup"
      :mate-mail="targetMateMail!"
      :enable-select-sender="false"
      :enable-select-recipient="false"
      @save="onSaveMail"
    />
  </div>
</template>
